import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Droplets, Save, ShoppingBag, Wheat } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, errMsg, eur, img } from "@/lib/api";
import { MeasureHelp, estimateWeight } from "@/pages/HorseWeight";
import { saveHorseWeight } from "@/components/DoseByWeight";
import { SaveHorseWeight } from "@/components/SaveHorseWeight";

const OPTS = {
  age: [["potro", "Potro (menos de 4 años)"], ["adulto", "Adulto"], ["senior", "Senior (+18 años)"]],
  breed: [["pre", "PRE / Pura Raza Española"], ["arabe", "Árabe"], ["pura_sangre", "Pura sangre inglés"], ["warmblood", "Caballo de deporte / Warmblood"], ["cuarto_milla", "Cuarto de milla"], ["cob_poni", "Cob o raza rústica"], ["otro", "Otra / cruzado"]],
  activity: [["reposo", "Reposo"], ["paseo", "Paseo"], ["ligero", "Trabajo ligero"], ["medio", "Trabajo medio"], ["competicion", "Competición"], ["gestante", "Yegua gestante"], ["lactante", "Yegua lactante"], ["potro", "Potro en crecimiento"]],
  condition: [["delgado", "Delgado"], ["normal", "Normal"], ["sobrepeso", "Con sobrepeso"]],
};
const LBL = { age: "Edad", breed: "Raza o tipo", activity: "Actividad", condition: "Estado físico" };

const Pick = ({ k, value, onChange }) => (
  <div>
    <Label>{LBL[k]}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5 h-11" data-testid={`diet-${k}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{OPTS[k].map(([v, l]) => <SelectItem key={v} value={v} data-testid={`diet-${k}-option-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Stat = ({ icon: Icon, label, value, unit, testid }) => (
  <div className="bg-white rounded p-5 border border-blue-100">
    <Icon className="text-[#DC2626]" size={22} />
    <p className="text-xs uppercase tracking-wider text-slate-500 mt-3 font-bold">{label}</p>
    <p className="font-display font-black text-4xl text-[#0B1D3A] mt-1" data-testid={testid}>{value}<span className="text-lg ml-1">{unit}</span></p>
  </div>
);

const SupplementRow = ({ s }) => {
  const { add } = useCart();
  const [fmt, setFmt] = useState(s.formats[0]);
  return (
    <div className="flex flex-col sm:flex-row gap-4 border border-slate-200 rounded p-4 bg-white" data-testid={`diet-supplement-${s.slug}`}>
      <img src={img(s.image)} alt={s.name} className="w-20 h-20 object-contain bg-[#EFF6FF] rounded" />
      <div className="flex-1">
        <Link to={`/producto/${s.slug}`} className="font-display font-extrabold text-xl text-[#0B1D3A] hover:text-[#DC2626]">{s.name}</Link>
        <p className="text-sm text-slate-600">{s.why}</p>
        <p className="text-sm mt-1"><b className="text-[#1E3A8A]">Dosis:</b> {s.dose}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {s.formats.map((f) => <button key={f.id} onClick={() => setFmt(f)} className={`text-xs px-2.5 py-1 rounded-full border ${fmt.id === f.id ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300"}`}>{f.label} · {eur(f.price)}</button>)}
        </div>
      </div>
      <button onClick={() => add({ slug: s.slug, name: s.name, images: [s.image] }, fmt)} data-testid={`diet-add-${s.slug}`}
        className="self-start sm:self-center flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold px-4 py-2.5 rounded-full">
        <ShoppingBag size={15} /> Añadir
      </button>
    </div>
  );
};

export default function DietCalculator() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("weight");
  const [f, setF] = useState({ horse_name: "", weight: params.get("peso") || "500", height_cm: "160", girth_cm: "185", age: "adulto", breed: "pre", activity: "ligero", condition: "normal" });
  const [res, setRes] = useState(null);
  const [input, setInput] = useState(null);
  const [busy, setBusy] = useState(false);

  const calc = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { age: f.age, breed: f.breed, activity: f.activity, condition: f.condition, horse_name: f.horse_name || null,
      ...(mode === "weight" ? { weight: Number(f.weight) } : { height_cm: Number(f.height_cm), girth_cm: Number(f.girth_cm) }) };
    try { const { data } = await api.post("/diet/calculate", body); setRes(data); saveHorseWeight(data.weight_kg); setInput(body); setTimeout(() => document.getElementById("diet-result")?.scrollIntoView({ behavior: "smooth" }), 50); }
    catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  const save = async () => {
    try { await api.post("/diet/save", { input, result: res }); toast.success("Dieta guardada en tu cuenta"); }
    catch (e2) { toast.error(errMsg(e2)); }
  };

  return (
    <div data-testid="diet-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="text-[#60A5FA] font-bold text-sm tracking-[0.2em] uppercase">Gratis · Sin cuenta</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl mt-2">Calculadora de dieta FAUNNY</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Calcula la ración diaria de tu caballo según su peso, edad, raza y actividad, y descubre qué complementos FAUNNY le ayudan.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-12 grid lg:grid-cols-[420px_1fr] gap-10">
        <form onSubmit={calc} className="space-y-5 h-fit lg:sticky lg:top-28">
          <div><Label>Nombre del caballo (opcional)</Label><Input value={f.horse_name} onChange={(e) => setF({ ...f, horse_name: e.target.value })} data-testid="diet-name-input" className="mt-1.5 h-11" /></div>
          <div className="flex bg-[#EFF6FF] rounded-full p-1">
            {[["weight", "Conozco su peso"], ["measures", "Estimar por medidas"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setMode(v)} data-testid={`diet-mode-${v}`}
                className={`flex-1 text-sm font-bold py-2 rounded-full transition-colors ${mode === v ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>{l}</button>
            ))}
          </div>
          {mode === "weight" ? (
            <div><Label>Peso (kg)</Label><Input type="number" min="80" max="1200" required value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} data-testid="diet-weight-input" className="mt-1.5 h-11" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Altura a la cruz (cm)</Label><Input type="number" min="80" max="210" required value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} data-testid="diet-height-input" className="mt-1.5 h-11" /></div>
              <div><Label>Perímetro torácico (cm)</Label><Input type="number" min="90" max="260" required value={f.girth_cm} onChange={(e) => setF({ ...f, girth_cm: e.target.value })} data-testid="diet-girth-input" className="mt-1.5 h-11" /></div>
              <p className="col-span-2 text-sm text-[#0B1D3A]" data-testid="diet-live-weight">Peso estimado: <b>{estimateWeight(f.girth_cm, f.height_cm, f.age === "potro") ?? "—"} kg</b> <span className="text-slate-500">({f.age === "potro" ? "fórmula menores de 4 años" : "fórmula 4 años o más"})</span></p>
              <div className="col-span-2"><MeasureHelp /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {["age", "condition"].map((k) => <Pick key={k} k={k} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} />)}
          </div>
          {["breed", "activity"].map((k) => <Pick key={k} k={k} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} />)}
          <button disabled={busy} data-testid="diet-calculate-button" className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold text-lg py-3.5 rounded-full transition-colors">
            {busy ? "Calculando…" : "Calcular ración"}
          </button>
        </form>

        <div id="diet-result">
          {!res ? (
            <div className="border-2 border-dashed border-slate-200 rounded p-10 text-center text-slate-500 h-full grid place-items-center" data-testid="diet-placeholder">
              Rellena los datos de tu caballo y pulsa "Calcular ración".
            </div>
          ) : (
            <div className="space-y-8 rise" data-testid="diet-result">
              <div className="bg-[#EFF6FF] rounded p-6">
                <div className="flex flex-wrap justify-between gap-3 items-end">
                  <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B1D3A]">Ración diaria {input.horse_name ? `de ${input.horse_name}` : ""}</h2>
                  <p className="text-sm text-slate-600" data-testid="diet-weight-result">{res.weight_estimated ? "Peso estimado" : "Peso"}: <b>{res.weight_kg} kg</b> · {res.activity_label}</p>
                </div>
                <SaveHorseWeight kg={res.weight_kg} source="dieta" defaultName={input.horse_name} id="diet-weight-save" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                  <Stat icon={Wheat} label="Forraje (heno/pasto)" value={res.forage_kg} unit="kg" testid="diet-forage" />
                  <Stat icon={Wheat} label="Pienso / concentrado" value={res.concentrate_kg} unit="kg" testid="diet-concentrate" />
                  <Stat icon={Droplets} label="Agua" value={res.water_l} unit="L" testid="diet-water" />
                  <Stat icon={Droplets} label={res.electrolytes ? "Sal + electrolitos" : "Sal"} value={res.salt_g} unit="g" testid="diet-salt" />
                </div>
              </div>
              <div>
                <h3 className="font-display font-extrabold text-2xl text-[#0B1D3A]">Tipo de comida recomendado</h3>
                <ul className="mt-3 space-y-2">{res.foods.map((x) => <li key={x} className="flex gap-2 text-slate-700"><span className="text-[#DC2626] font-bold">→</span>{x}</li>)}</ul>
              </div>
              <div>
                <h3 className="font-display font-extrabold text-2xl text-[#0B1D3A]">Complementos FAUNNY para su perfil</h3>
                <div className="space-y-3 mt-4">{res.supplements.map((s) => <SupplementRow key={s.slug} s={s} />)}</div>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                {user ? (
                  <button onClick={save} data-testid="diet-save-button" className="flex items-center gap-2 bg-[#0B1D3A] text-white font-display font-bold px-6 py-3 rounded-full"><Save size={17} /> Guardar en mi cuenta</button>
                ) : (
                  <Link to="/login?next=/calculadora" className="text-sm font-bold text-[#2563EB]" data-testid="diet-login-to-save">Inicia sesión para guardar este resultado →</Link>
                )}
              </div>
              <p className="text-xs text-slate-500 flex gap-2 bg-slate-50 p-3 rounded" data-testid="diet-disclaimer">
                <AlertTriangle size={16} className="shrink-0 text-[#DC2626]" /> Orientación general basada en pautas de nutrición equina. No sustituye al veterinario ni al nutricionista equino.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
